import React, { useState, useEffect } from "react";
import api from "../../api/axiosConfig";
import {
  Card,
  Descriptions,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Spin,
  Row,
  Col,
  Typography,
  Divider,
  Table,
  Alert,
} from "antd";

const { Title } = Typography;
const { Option } = Select;

const AccountPage = () => {
  const [user, setUser] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isChangePasswordModalVisible, setIsChangePasswordModalVisible] = useState(false);
  const [passwordChanged, setPasswordChanged] = useState(false);
  const [loading, setLoading] = useState(true);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();

  useEffect(() => {
    const fetchUserDetailsAndBookings = async () => {
      setLoading(true);
      const token = localStorage.getItem("token");
      if (token) {
        try {
          const userResponse = await api.get("/user/account");
          setUser(userResponse.data);
          form.setFieldsValue(userResponse.data);

          const bookingsResponse = await api.get("/user/bookings");
          setBookings(bookingsResponse.data);
        } catch (error) {
          message.error("Failed to load user details or bookings.");
        }
      }
      setLoading(false);
    };
    fetchUserDetailsAndBookings();
  }, [form]);

  const handleUpdateSubmit = async (values) => {
    try {
      await api.put("/user/account", values);
      setUser({ ...user, ...values });
      setIsEditing(false);
      message.success("Account updated successfully.");
    } catch (error) {
      message.error("Failed to update account.");
    }
  };

  const handleChangePasswordSubmit = async (values) => {
    setPasswordLoading(true);
    try {
      await api.post("/user/change-password", values);
      setPasswordChanged(true);
      message.success("Password changed successfully!");
      passwordForm.resetFields();
      setIsChangePasswordModalVisible(false);
    } catch (error) {
      message.error(
        error.response?.data?.error || "Failed to change password."
      );
    } finally {
        setPasswordLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", marginTop: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!user) {
    return (
      <div style={{ textAlign: "center", marginTop: "50px" }}>
        <Title level={3}>Please log in to view your account.</Title>
      </div>
    );
  }

  return (
    <Row justify="center" style={{ marginTop: "20px" }}>
      <Col xs={24} sm={20} md={16} lg={12} xl={10}>
        <Card>
          <Title level={2}>My Account</Title>
          {passwordChanged && (
            <Alert
              message="Password changed successfully"
              type="success"
              showIcon
              closable
              onClose={() => setPasswordChanged(false)}
              style={{ marginBottom: "20px" }}
            />
          )}
          <Descriptions bordered column={1}>
            <Descriptions.Item label="Name">{user.name}</Descriptions.Item>
            <Descriptions.Item label="Age">{user.age}</Descriptions.Item>
            <Descriptions.Item label="Gender">{user.gender}</Descriptions.Item>
            <Descriptions.Item label="Phone Number">
              {user.phone_number}
            </Descriptions.Item>
          </Descriptions>
          <Button
            type="primary"
            onClick={() => setIsEditing(true)}
            style={{ marginTop: "20px" }}
          >
            Edit Profile
          </Button>
          <Button
            onClick={() => setIsChangePasswordModalVisible(true)}
            style={{ marginTop: "20px", marginLeft: "10px" }}
          >
            Change Password
          </Button>

          <Divider />

          <Title level={3}>My Bookings</Title>
          <Table
            columns={[
              { title: 'Rehab Center', dataIndex: 'rehab_center_name', key: 'rehab_center_name' },
              { title: 'Service', dataIndex: 'service', key: 'service' },
              { title: 'Date', dataIndex: 'booking_date', key: 'booking_date', render: (text) => new Date(text).toLocaleDateString() },
              { title: 'Time', dataIndex: 'booking_time', key: 'booking_time' },
            ]}
            dataSource={bookings}
            rowKey="id"
            pagination={false}
          />
        </Card>
      </Col>
      <Modal
        title="Edit Profile"
        open={isEditing}
        onCancel={() => setIsEditing(false)}
        onOk={() => form.submit()}
      >
        <Form form={form} onFinish={handleUpdateSubmit} layout="vertical">
          <Form.Item name="name" label="Name" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="age" label="Age" rules={[{ required: true }]}><Input type="number" /></Form.Item>
          <Form.Item name="gender" label="Gender" rules={[{ required: true }]}>
            <Select>
              <Option value="male">Male</Option>
              <Option value="female">Female</Option>
              <Option value="other">Other</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title="Change Password"
        open={isChangePasswordModalVisible}
        onCancel={() => {
            setIsChangePasswordModalVisible(false);
            passwordForm.resetFields();
        }}
        footer={[
            <Button key="back" onClick={() => {
                setIsChangePasswordModalVisible(false);
                passwordForm.resetFields();
            }}>
              Cancel
            </Button>,
            <Button key="submit" type="primary" loading={passwordLoading} onClick={() => passwordForm.submit()}>
              Change Password
            </Button>,
        ]}
      >
        <Form form={passwordForm} onFinish={handleChangePasswordSubmit} layout="vertical">
          <Form.Item
            name="oldPassword"
            label="Old Password"
            rules={[{ required: true, message: "Please input your old password!" }]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item
            name="newPassword"
            label="New Password"
            rules={[{ required: true, message: "Please input your new password!" }]}
          >
            <Input.Password />
          </Form.Item>
        </Form>
      </Modal>
    </Row>
  );
};

export default AccountPage;